/**
 * SumsubVerification - KYC/KYB verification widget using Sumsub WebSDK
 * Sandbox mode for testing merchant identity verification
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, RefreshCw, Shield } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SumsubVerification = ({ user, token }) => {
  const containerRef = useRef(null);
  const sdkRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading, ready, submitted, error
  const [errorMsg, setErrorMsg] = useState('');

  const getAccessToken = useCallback(async () => {
    const userId = user?.id || user?.email;
    
    const res = await fetch(`${API_URL}/api/sumsub/access-token`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ userId })
    });
    
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.detail || 'Failed to get access token');
    }
    
    const data = await res.json();
    return data.token;
  }, [user, token]);

  useEffect(() => {
    let mounted = true;

    async function launchSdk() {
      try {
        const accessToken = await getAccessToken();
        
        if (!mounted) return;

        // Check if snsWebSdk is available
        if (!window.snsWebSdk) {
          throw new Error('Sumsub SDK not loaded');
        }

        const sdk = window.snsWebSdk
          .init(accessToken, () => getAccessToken())
          .withConf({
            lang: 'en',
            email: user?.email || '',
            theme: 'dark',
          })
          .withOptions({
            addViewportTag: false,
            adaptIframeHeight: true,
          })
          .on('idCheck.onReady', () => {
            if (mounted) setStatus('ready');
          })
          .on('idCheck.onApplicantSubmitted', () => {
            if (mounted) setStatus('submitted');
          })
          .on('idCheck.onApplicantStatusChanged', (payload) => {
            console.log('Sumsub status changed:', payload);
          })
          .on('idCheck.onError', (error) => {
            console.error('Sumsub error:', error);
            if (mounted) {
              setStatus('error');
              setErrorMsg(error?.message || 'Verification encountered an error');
            }
          })
          .build();

        sdkRef.current = sdk;
        sdk.launch('#sumsub-websdk-container');
      } catch (err) {
        console.error('Failed to launch Sumsub SDK:', err);
        if (mounted) {
          setStatus('error');
          setErrorMsg(err.message || 'Failed to initialize verification. Please try again.');
        }
      }
    }

    // Load the SDK script if not already loaded
    if (!window.snsWebSdk) {
      const script = document.createElement('script');
      script.src = 'https://static.sumsub.com/idensic/static/sns-websdk-builder.js';
      script.async = true;
      script.onload = () => {
        if (mounted) launchSdk();
      };
      script.onerror = () => {
        if (mounted) {
          setStatus('error');
          setErrorMsg('Failed to load verification service.');
        }
      };
      document.head.appendChild(script);
    } else {
      launchSdk();
    }

    return () => {
      mounted = false;
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [user, getAccessToken]);

  const handleRetry = () => {
    setStatus('loading');
    setErrorMsg('');
    window.location.reload();
  };

  return (
    <Card className="border-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(212,175,55,0.1),transparent_50%)]" />
      <CardContent className="py-8 relative">
        {/* Header */}
        <div className="flex items-start gap-5 mb-6">
          <div className="p-3 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/20">
            <Shield className="w-6 h-6 text-slate-900" />
          </div>
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-emerald-400 text-xs font-semibold mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              SECURE VERIFICATION
            </div>
            <h3 className="text-xl font-bold mb-2 text-white">Identity Verification</h3>
            <p className="text-slate-300 leading-relaxed text-sm">
              Complete your KYC verification to activate your merchant account. Powered by Sumsub.
            </p>
          </div>
        </div>

        {/* Loading State */}
        {status === 'loading' && (
          <div className="flex flex-col items-center justify-center py-16 bg-slate-800/50 rounded-xl">
            <Loader2 className="w-10 h-10 animate-spin text-amber-400 mb-4" />
            <p className="text-slate-300">Loading verification...</p>
            <p className="text-slate-500 text-sm mt-1">This may take a few seconds</p>
          </div>
        )}

        {/* Submitted State */}
        {status === 'submitted' && (
          <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-emerald-300 font-semibold text-lg mb-2">Documents Submitted Successfully</h3>
            <p className="text-slate-400">
              Your verification is being reviewed. This typically takes a few minutes to a few hours.
            </p>
            <p className="text-slate-500 text-sm mt-4">
              You will receive an email notification once your verification is complete.
            </p>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-red-300 font-semibold text-lg mb-2">Verification Error</h3>
            <p className="text-slate-400 mb-4">{errorMsg}</p>
            <Button 
              onClick={handleRetry}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        )}

        {/* SDK Container - Only show when ready or loading */}
        <div 
          id="sumsub-websdk-container" 
          ref={containerRef}
          style={{ 
            minHeight: status === 'ready' ? '600px' : '0px',
            display: (status === 'submitted' || status === 'error') ? 'none' : 'block'
          }}
          className="rounded-xl overflow-hidden bg-slate-800/30"
        />

        {/* Security Notice */}
        <div className="mt-6 p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
          <p className="text-emerald-400 text-xs flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <strong>Secure:</strong> Your documents are encrypted and processed by Sumsub's secure verification system.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SumsubVerification;
